import React, { useEffect, useState } from "react";
import { MDBIcon } from "mdbreact";
import { get, post } from "api/index";

// Construction Cost Transparency: the seven-part breakdown of one catalogue
// unit cost, from CurLight (GET /data/v2/cost-breakdown/:collection/:id).
// Owner 2026-10-26 (option A): every part is filled from official averages -
// BEA's split of a construction dollar, Census AIES industry ratios - and
// labelled as an estimate; the catalogue's own material/labour figures are
// used where the row has them. A part whose source is not published shows
// "no source", never 0.
//
// THE LAYOUT IS THE DEMO'S (owner 2026-10-29: "the old demo data ui and fields
// and layouts respected as a template, filled with the correct data, links
// and charts"): header, the plain-English box, the supply-chain accordion,
// the summary cards, price history with best/likely/worst, what could change
// the price, and a trend per part. Every number is CurLight's: the history
// moves each part along its own official index, the projection extrapolates
// those indexes (said so on the chart), and nothing is invented to fill a gap.

const getBreakdown = (collection, id) =>
  get(`/data/v2/cost-breakdown/${collection}/${id}`, true);

// The average across companies (shown when a company has no price of its
// own) is computed per request and has no record: the table gives it a random
// _id, which CurLight refused as "Bad id". It is sent by its figures instead.
const AVERAGE_FIELDS = ["Code", "Description", "Unit", "Level", "CategoryName", "UnitCost", "MaterialUnitCost", "CrewUnitCost", "TotalComposedUnitCost"];
export const isRecordId = (v) => /^[0-9a-f]{24}$/i.test(String(v || ""));
export const averageBody = (item) => ({
  item: AVERAGE_FIELDS.reduce((o, k) => (item && item[k] != null ? { ...o, [k]: item[k] } : o), {}),
});
// An average is split live and never stored, so it has no version.
export const versionText = (d) =>
  d && d.version ? ` · Version ${d.version}${d.versions > 1 ? ` of ${d.versions}` : ""}` : " · split live, not stored";
const getAverageBreakdown = (collection, item) =>
  post(`/data/v2/cost-breakdown/${collection}/average`, averageBody(item), false, true);

// The table hands the panel a tree node (helpers/buildTree.js
// CompanyConvertFrom), not a catalogue row: its figures are camelCase
// (unitCost, materialUnitCost...), its code is Division, and the row behind
// it sits under Database. Reading UnitCost off it found nothing, so every
// average row ended at "no catalogue record" without asking CurLight.
const pick = (o, a, b) => (o && o[a] != null ? o[a] : o && o[b] != null ? o[b] : undefined);
export const rowFigures = (node) => ({
  Code: pick(node, "Code", "Division"),
  Description: node?.Description,
  Unit: node?.Unit,
  Level: node?.Level,
  CategoryName: node?.CategoryName,
  UnitCost: pick(node, "UnitCost", "unitCost"),
  MaterialUnitCost: pick(node, "MaterialUnitCost", "materialUnitCost"),
  CrewUnitCost: pick(node, "CrewUnitCost", "crewUnitCost"),
  TotalComposedUnitCost: pick(node, "TotalComposedUnitCost", "totalComposedUnitCost"),
});
const priced = (f) => Number(f.UnitCost) > 0 || Number(f.TotalComposedUnitCost) > 0;

// Which request answers a node, first match wins: its own record; its own
// figures (an average has no record); the row behind it; and, with no price
// anywhere, its words alone - CurLight then maps it to each part's typical
// share. Null only when there is nothing to map.
export const requestFor = (node) => {
  if (!node) return null;
  const own = node._id != null ? node._id : node.Guid;
  if (isRecordId(own)) return { id: String(own) };
  const f = rowFigures(node);
  if (priced(f)) return { figures: f };
  const db = node.Database;
  const dbId = db && (db._id != null ? db._id : db.Guid);
  if (isRecordId(dbId)) return { id: String(dbId) };
  if (db && priced(rowFigures(db))) return { figures: rowFigures(db) };
  if (f.Description || f.Code) return { figures: f };
  return null;
};

// The demo's seven parts, its colours and its icons, in its order.
const PART_STYLE = {
  rawMaterials: { color: "#0d6efd", icon: "💎", short: "Raw materials" },
  manufacturing: { color: "#6610f2", icon: "🏭", short: "Manufacturing" },
  transportation: { color: "#198754", icon: "🚚", short: "Transportation" },
  storeIssue: { color: "#6f42c1", icon: "🏪", short: "Store issue" },
  equipment: { color: "#0dcaf0", icon: "⚙️", short: "Equipment" },
  labour: { color: "#ffc107", icon: "👷", short: "Labour" },
  contractorFees: { color: "#212529", icon: "🏢", short: "Contractor" },
};
const SUPPLY = ["rawMaterials", "manufacturing", "transportation", "storeIssue"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Period codes from the feed: 2026-M09, 2025-A, 2026-10-19 (week), 2027-03-V (valid to), 2026-Q3.
export function periodLabel(p) {
  let m;
  if ((m = /^(\d{4})-M(\d{2})$/.exec(p || ""))) return `${MONTHS[+m[2] - 1]} ${m[1]}`;
  if ((m = /^(\d{4})-A$/.exec(p || ""))) return `Year ${m[1]}`;
  if ((m = /^(\d{4})-(\d{2})-V$/.exec(p || ""))) return `valid to ${MONTHS[+m[2] - 1]} ${m[1]}`;
  if ((m = /^(\d{4})-Q(\d)$/.exec(p || ""))) return `${m[1]}-Q${m[2]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(p || "")) return `week of ${p}`;
  return p || "–";
}

const money = (v) =>
  v === null || v === undefined
    ? null
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (v) => {
  if (v === null || v === undefined) return "–";
  const r = Math.round(Number(v) * 10) / 10;
  return `${r > 0 ? "+" : ""}${(r === 0 ? 0 : r).toFixed(1)}%`; // never "-0.0%"
};

// A reference value with its unit, as the source publishes it.
function refValue(r) {
  if (r.value === null || r.value === undefined) return "🔒";
  const v = Number(r.value);
  switch (r.unit) {
    case "usd_per_hour":
      return `${money(v)} /h`;
    case "usd_per_gallon":
      return `$${v.toFixed(3)} /gal`;
    case "usd_per_metric_ton":
      return `${money(v)} /t`;
    default:
      if (/^pct_/.test(r.unit || "")) return `${v.toFixed(2)} %`;
      return `${v} index`;
  }
}

const Source = ({ s }) =>
  s && s.evidenceUrl ? (
    <a href={s.evidenceUrl} target="_blank" rel="noopener noreferrer">
      {s.name}
    </a>
  ) : (
    <span>{(s && s.name) || "–"}</span>
  );

const confidenceClass = (c) => (c === "high" ? "badge-success" : c === "medium" ? "badge-warning" : c === "low" ? "badge-secondary" : "badge-light");

// ---- Charts (plain SVG, like the demo: no chart library on this page) ----

// History (solid) + best / likely / worst (dashed) on one value axis.
export function PriceChart({ history, projection, locked }) {
  const W = 640;
  const H = 260;
  const L = 56;
  const R = 28;
  const T = 16;
  const B = 34;
  const cols = history.length + projection.length;
  if (!history.length || cols < 2) return null;
  const values = [];
  history.forEach((h) => typeof h.value === "number" && values.push(h.value));
  projection.forEach((p) => ["best", "likely", "worst"].forEach((k) => typeof p[k] === "number" && values.push(p[k])));
  if (locked || !values.length) {
    return <p className="text-muted small mb-0">🔒 The price history is shown to members.</p>;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.15 || max * 0.05 || 1;
  const lo = Math.max(0, min - pad);
  const hi = max + pad;
  const x = (i) => L + (i / (cols - 1)) * (W - L - R);
  const y = (v) => T + (1 - (v - lo) / (hi - lo)) * (H - T - B);
  const todayIdx = history.length - 1;
  const path = (pts) => pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const hist = history.map((h, i) => [x(i), y(h.value)]);
  const proj = (k) => [[x(todayIdx), y(history[todayIdx].value)]].concat(projection.map((p, j) => [x(todayIdx + 1 + j), y(p[k])]));
  const ticks = [0, 1, 2, 3, 4].map((t) => lo + ((hi - lo) * t) / 4);
  const lines = [
    { k: "best", color: "#198754" },
    { k: "likely", color: "#ffc107" },
    { k: "worst", color: "#dc3545" },
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Price history and projections">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="#e9ecef" />
          <text x={L - 6} y={y(t) + 4} fontSize="11" textAnchor="end" fill="#6c757d">
            ${t >= 100 ? Math.round(t) : t.toFixed(2)}
          </text>
        </g>
      ))}
      <line x1={x(todayIdx)} x2={x(todayIdx)} y1={T} y2={H - B} stroke="#ffc107" strokeDasharray="3 3" />
      <text x={x(todayIdx)} y={T + 10} fontSize="11" textAnchor="middle" fill="#997404" fontWeight="700">
        TODAY
      </text>
      {lines.map((l) => (
        <path key={l.k} d={path(proj(l.k))} fill="none" stroke={l.color} strokeWidth={l.k === "likely" ? 2.5 : 1.8} strokeDasharray="6 4" />
      ))}
      <path d={path(hist)} fill="none" stroke="#0d6efd" strokeWidth="2.5" />
      {hist.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === todayIdx ? 5 : 3.5} fill={i === todayIdx ? "#ffc107" : "#0d6efd"} stroke="#fff" strokeWidth="1.5">
          <title>{`${history[i].period}: ${money(history[i].value)}`}</title>
        </circle>
      ))}
      {projection.map((p, j) =>
        lines.map((l) => (
          <circle key={`${j}${l.k}`} cx={x(todayIdx + 1 + j)} cy={y(p[l.k])} r="3" fill={l.color}>
            <title>{`${p.period} ${l.k}: ${money(p[l.k])}`}</title>
          </circle>
        ))
      )}
      {history.concat(projection).map((p, i) => (
        <text key={p.period} x={x(i)} y={H - 12} fontSize="10" textAnchor="middle" fill={i === todayIdx ? "#0d6efd" : "#6c757d"} fontWeight={i >= todayIdx ? "700" : "400"}>
          {p.period}
        </text>
      ))}
    </svg>
  );
}

// One part's trend: its amount (or index) over the quarters, then its projection.
function MiniChart({ trend, color, locked }) {
  const pts = trend.points.filter((p) => typeof p.value === "number");
  if (locked) return <div className="text-muted small py-3">🔒 members only</div>;
  if (pts.length < 2) return <div className="text-muted small py-3">{trend.held ? "No monthly index for this part yet - held at today's amount." : "One period only."}</div>;
  const W = 300;
  const H = 90;
  const vals = pts.map((p) => p.value).concat(typeof trend.projected === "number" ? [trend.projected] : []);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || Math.abs(max) * 0.05 || 1;
  const n = pts.length + (typeof trend.projected === "number" ? 1 : 0);
  const x = (i) => 6 + (i / (n - 1)) * (W - 12);
  const y = (v) => 8 + (1 - (v - min) / span) * (H - 22);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const last = pts.length - 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="90" preserveAspectRatio="none" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="2" />
      {typeof trend.projected === "number" ? (
        <path d={`M${x(last)},${y(pts[last].value)} L${x(last + 1)},${y(trend.projected)}`} fill="none" stroke={color} strokeWidth="2" strokeDasharray="4 4" opacity="0.6" />
      ) : null}
      {pts.map((p, i) => (
        <circle key={p.period} cx={x(i)} cy={y(p.value)} r={i === last ? 4.5 : 2.5} fill={i === last ? "#ffc107" : color} stroke="#fff" strokeWidth="1">
          <title>{`${p.period}: ${trend.now === null && !locked ? p.value : money(p.value)}`}</title>
        </circle>
      ))}
      <text x={x(last)} y={y(pts[last].value) - 8} fontSize="9" textAnchor="middle" fill="#6c757d" fontWeight="700">
        NOW
      </text>
    </svg>
  );
}

// ---- The panel ----

const ConstructionCostDashboard = ({ item, collection, context }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState({});
  const request = requestFor(item);
  const id = request && request.id;
  const figuresKey = request && request.figures ? JSON.stringify(averageBody(request.figures)) : "";

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError("");
    if (!request || !collection) {
      setError("This item has no catalogue record or unit cost to break down.");
      return undefined;
    }
    (id ? getBreakdown(collection, id) : getAverageBreakdown(collection, request.figures))
      .then((res) => {
        if (cancelled) return;
        if (!res || !Array.isArray(res.components)) {
          setError((res && res.err) || "The cost breakdown is unavailable right now.");
          return;
        }
        setData(res);
      })
      .catch(() => !cancelled && setError("The cost breakdown is unavailable right now."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, figuresKey, collection]);

  if (error) {
    return (
      <div className="text-center text-muted py-5">
        <MDBIcon icon="info-circle" size="2x" className="mb-3" />
        <p>{error}</p>
      </div>
    );
  }
  if (!data) return <p className="text-muted py-4">Loading the cost breakdown…</p>;

  const parts = data.components;
  const byKey = Object.fromEntries(parts.map((c) => [c.key, c]));
  const shareOf = (c) => (!c ? null : typeof c.sharePct === "number" ? c.sharePct : c.typicalSharePct);
  const locked = !!data.locked;
  const o = data.outlook;
  const description = item?.Description || "Catalogue item";
  const code = item?.Division || item?.Code || "";
  const unit = data.unit || item?.Unit || "unit";
  const where = [context?.City, context?.State].filter(Boolean).join(", ");
  const kind = [context?.ProjectType, context?.LaborType].filter(Boolean).join(" - ");
  const sum = (keys) => {
    const vals = keys.map((k) => byKey[k] && byKey[k].value).filter((v) => typeof v === "number");
    return vals.length ? vals.reduce((a, v) => a + v, 0) : null;
  };
  const pct = (v) => (data.total && typeof v === "number" ? (v / data.total) * 100 : null);
  const cards = [
    { title: "Supply chain", note: "raw + manufacturing + freight + store", value: sum(SUPPLY), share: data.total ? pct(sum(SUPPLY)) : SUPPLY.reduce((a, k) => a + (shareOf(byKey[k]) || 0), 0) || null, cls: "primary" },
    { title: "Equipment", note: "rental and leasing", value: sum(["equipment"]), share: data.total ? pct(sum(["equipment"])) : shareOf(byKey.equipment), cls: "info" },
    { title: "Labour", note: "trade crews", value: sum(["labour"]), share: data.total ? pct(sum(["labour"])) : shareOf(byKey.labour), cls: "warning" },
    { title: "Contractor", note: "overhead and profit", value: sum(["contractorFees"]), share: data.total ? pct(sum(["contractorFees"])) : shareOf(byKey.contractorFees), cls: "success" },
  ];
  const references = [];
  const seenRef = {};
  parts.forEach((c) =>
    (c.references || []).forEach((r) => {
      if (seenRef[r.sourceId]) return;
      seenRef[r.sourceId] = true;
      references.push({ ...r, part: c.label });
    })
  );
  const trendOf = (key) => o && o.partTrends && o.partTrends.find((t) => t.key === key);

  return (
    <div className="py-2 cct-panel">
      {/* The page's global styles capitalise .text-muted/.small and underline
          button text inside the side panel (seen on staging 2026-10-29); the
          panel reads as the demo did, so they are switched off in here. */}
      <style>{`.cct-panel .text-muted, .cct-panel .small, .cct-panel .cct-row, .cct-panel .cct-row * { text-transform: none !important; }
.cct-panel .cct-row, .cct-panel .cct-row * { text-decoration: none !important; }
.cct-panel .text-muted { color: #6c757d !important; }
.cct-panel .cct-row { cursor: pointer; }
.cct-panel .cct-row:focus { outline: 2px solid #0d6efd55; outline-offset: -2px; }`}</style>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: 16 }}>
        <div>
          <h4 className="mb-1" style={{ fontWeight: 600 }}>
            {description}
          </h4>
          <div className="text-muted">
            Code: {code || "–"}
            {item?.CategoryName ? ` | ${item.CategoryName}` : ""} · Material family: {data.family}
          </div>
          {where || kind ? (
            <div className="text-muted small">
              {where}
              {where && kind ? " • " : ""}
              {kind}
            </div>
          ) : null}
          <div className="text-muted small">
            {data.priceOf ? (
              <>
                Price broken down: <b>{data.priceOf.owner}</b>
                {data.priceOf.version ? ` (${data.priceOf.version})` : ""} ·{" "}
              </>
            ) : null}
            Data as of <b>{data.oldestSourceAsOf || "–"}</b> (oldest source)
            {versionText(data)}
          </div>
        </div>
        <div className="text-right" style={{ whiteSpace: "nowrap" }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#0d6efd" }}>
            {data.total !== null ? money(data.total) : data.sharesOnly || !locked ? "No price" : "🔒"}
          </div>
          <div className="text-muted small">per {unit} · USD</div>
        </div>
      </div>

      {locked ? (
        <div className="alert alert-light border mb-3">
          <MDBIcon icon="lock" className="mr-2" />
          Sources, methods and dates are shown; the amounts are for members.
        </div>
      ) : null}

      {/* What this means in plain English */}
      <div className="alert alert-info mb-4" style={{ fontSize: 14 }}>
        <h6 style={{ fontWeight: 600 }}>What This Means in Plain English</h6>
        {data.total !== null && !locked ? (
          <p className="mb-2">
            Right now, {description.toLowerCase()} costs <b>{money(data.total)}</b> per {unit}.
          </p>
        ) : data.sharesOnly ? (
          <p className="mb-2">
            This item has no unit cost on record, so there is no price to split. Each part shows its <b>typical share</b> of a
            U.S. construction dollar for this material family (BEA input-output accounts, Census industry surveys).
          </p>
        ) : null}
        <p className="mb-1">{data.total !== null ? "Why this price? Out of every dollar you pay:" : "Out of a typical construction dollar:"}</p>
        <div className="row small mb-2">
          {parts.map((c) => (
            <div key={c.key} className="col-6">
              {PART_STYLE[c.key].icon}{" "}
              {locked
                ? "🔒"
                : c.value !== null
                ? money(c.value)
                : typeof c.typicalSharePct === "number"
                ? `${c.typicalSharePct}%`
                : "no source"}{" "}
              = {PART_STYLE[c.key].short}
            </div>
          ))}
        </div>
        {o && o.nextYear && !locked ? (
          <p className="mb-0">
            What happens next? If each part's price index keeps moving as it did over the last 12 months, this price would
            change by <b className="text-success">{signed(o.nextYear.bestPct)}</b> to{" "}
            <b className="text-danger">{signed(o.nextYear.worstPct)}</b> over the next year (likely{" "}
            <b>{signed(o.nextYear.likelyPct)}</b>). An extrapolation, not a forecast.
          </p>
        ) : null}
      </div>

      {data.estimated ? (
        <div className="alert alert-warning mb-3" style={{ fontSize: 13 }}>
          Parts marked <b>estimated</b> split this unit cost with official U.S. construction averages (BEA input-output
          accounts, Census industry surveys). They are not quotes for this item. Parts marked <b>catalogue</b> are this item's
          own figures.
        </div>
      ) : null}

      {/* Full supply chain breakdown - accordion */}
      <h6 style={{ fontWeight: 600 }}>Full Supply Chain Breakdown</h6>
      <div className="mb-2">
        {parts.map((c) => {
          const share = shareOf(c);
          const t = trendOf(c.key);
          const isOpen = !!open[c.key];
          return (
            <div key={c.key} className="border rounded mb-2" style={{ borderLeft: `4px solid ${PART_STYLE[c.key].color}` }}>
              <div
                role="button"
                tabIndex={0}
                className="cct-row text-dark p-2"
                onClick={() => setOpen((s) => ({ ...s, [c.key]: !s[c.key] }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpen((s) => ({ ...s, [c.key]: !s[c.key] }));
                  }
                }}
                aria-expanded={isOpen}
                data-testid={`part-${c.key}`}
              >
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
                  <MDBIcon icon={isOpen ? "chevron-down" : "chevron-right"} className="text-muted" />
                  <div style={{ flex: "1 1 200px" }}>
                    <b>
                      {PART_STYLE[c.key].icon} {c.label}
                    </b>
                    <div className="text-muted small">{c.actor}</div>
                  </div>
                  <div className="text-right" style={{ minWidth: 110 }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>
                      {locked ? "🔒" : c.value === null ? <span className="text-muted small">no value</span> : money(c.value)}
                    </div>
                    <div className="text-muted small">
                      {locked ? "" : typeof c.sharePct === "number" ? `${c.sharePct}% of total` : typeof c.typicalSharePct === "number" ? `${c.typicalSharePct}% typical` : ""}
                    </div>
                  </div>
                  {typeof c.markupPct === "number" ? (
                    <span className="badge badge-warning" style={{ fontWeight: 500 }}>
                      +{c.markupPct}% markup
                    </span>
                  ) : null}
                  {c.confidence ? (
                    <span className={`badge ${confidenceClass(c.confidence)}`} style={{ fontWeight: 500 }}>
                      {c.confidence} confidence
                    </span>
                  ) : null}
                </div>
                {share > 0 && !locked ? (
                  <div className="mt-1" style={{ height: 4, background: "#eef0f3", borderRadius: 2 }}>
                    <div style={{ width: `${Math.min(100, share)}%`, height: 4, background: PART_STYLE[c.key].color, borderRadius: 2 }} />
                  </div>
                ) : null}
              </div>
              {isOpen ? (
                <div className="px-3 pb-2 small" data-testid={`part-${c.key}-detail`}>
                  <div>
                    <span className="text-muted">How: </span>
                    {c.method}
                    <span className="text-muted"> · data for </span>
                    {c.sourceAsOf || "–"}
                  </div>
                  <div>
                    <span className="text-muted">Source: </span>
                    {(c.sources || []).length
                      ? c.sources.map((s, i) => (
                          <span key={s.sourceId}>
                            {i ? " · " : ""}
                            <Source s={s} /> <span className="text-muted">({periodLabel(s.sourceAsOf)})</span>
                          </span>
                        ))
                      : <span className="text-muted">not published yet</span>}
                  </div>
                  {(c.references || []).length ? (
                    <div>
                      <span className="text-muted">Reference prices: </span>
                      {c.references.map((r, i) => (
                        <span key={r.sourceId}>
                          {i ? " · " : ""}
                          <Source s={r} /> {refValue(r)} <span className="text-muted">({periodLabel(r.sourceAsOf)})</span>
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {t && t.driver ? (
                    <div>
                      <span className="text-muted">Price index it follows: </span>
                      <Source s={t.driver} />
                      {typeof t.change12Pct === "number" ? ` · ${signed(t.change12Pct)} over 12 months` : ""}
                      {t.driver.latestPeriod ? <span className="text-muted"> ({periodLabel(t.driver.latestPeriod)})</span> : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="small mb-4">
        {data.sharesOnly ? (
          <span className="text-muted">No unit cost on record, so the parts have shares but no amounts.</span>
        ) : data.totalCheck ? (
          data.totalCheck.ok ? (
            <span className="text-success">
              <MDBIcon icon="check" className="mr-1" />
              The seven parts add up to the unit cost.
            </span>
          ) : (
            <span className="text-danger">
              The parts add up to {money(data.totalCheck.sum)}, not {money(data.totalCheck.total)}.
            </span>
          )
        ) : (
          <span className="text-muted">Not every part has a value, so the parts are not checked against the unit cost.</span>
        )}
      </div>

      {/* Cost summary cards */}
      <div className="row mb-4">
        {cards.map((k) => (
          <div key={k.title} className="col-6 col-md-3 mb-2">
            <div className={`card border-${k.cls} h-100`} style={{ boxShadow: "none" }}>
              <div className="card-body p-2">
                <div className="small text-muted">{k.title}</div>
                <div className={`text-${k.cls === "warning" ? "dark" : k.cls}`} style={{ fontWeight: 700, fontSize: 18 }}>
                  {locked ? "🔒" : k.value !== null ? money(k.value) : typeof k.share === "number" ? `${k.share.toFixed(1)}%` : "–"}
                </div>
                <div className="small text-muted">
                  {!locked && k.value !== null && typeof k.share === "number" ? `${k.share.toFixed(1)}% · ` : ""}
                  {k.note}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Price history + projections */}
      {o && o.history && o.history.length ? (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-baseline flex-wrap">
            <h6 style={{ fontWeight: 600 }}>Price History and Future Projections</h6>
            <div className="small">
              <span style={{ color: "#0d6efd" }}>■</span> Actual <span style={{ color: "#198754" }}>■</span> Best{" "}
              <span style={{ color: "#ffc107" }}>■</span> Likely <span style={{ color: "#dc3545" }}>■</span> Worst
            </div>
          </div>
          <div className="border rounded p-2" style={{ background: "linear-gradient(to bottom, #f8f9fa, white)" }}>
            <PriceChart history={o.history} projection={o.projection} locked={locked} />
          </div>
          <div className="text-muted small mt-1">{o.basis}</div>
          {o.heldFlat && o.heldFlat.length ? (
            <div className="text-muted small">Held at today's amount (no monthly index yet): {o.heldFlat.join(", ")}.</div>
          ) : null}
        </div>
      ) : null}

      {/* What could change these prices */}
      {o && o.drivers && o.drivers.length ? (
        <div className="mb-4">
          <h6 style={{ fontWeight: 600 }}>What Could Change These Prices?</h6>
          <div className="row">
            {o.drivers.map((d) => (
              <div key={d.key} className="col-sm-6 mb-2">
                <div className="card h-100" style={{ boxShadow: "none", borderLeft: `4px solid ${PART_STYLE[d.key].color}` }}>
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between">
                      <b>{PART_STYLE[d.key].short}</b>
                      <span className={`badge ${d.change12Pct > 0 ? "badge-danger" : "badge-success"}`}>
                        {locked ? "🔒" : `${signed(d.change12Pct)} in 12 months`}
                      </span>
                    </div>
                    {!locked && typeof d.effect === "number" ? (
                      <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {d.effect >= 0 ? "+" : "−"}
                        {money(Math.abs(d.effect))} <span className="small text-muted">on this item over the year</span>
                      </div>
                    ) : null}
                    <div className="small">
                      <Source s={d.source} />
                      {!locked && typeof d.changeLastPct === "number" ? <span className="text-muted"> · last month {signed(d.changeLastPct)}</span> : null}
                      {d.source && d.source.latestPeriod ? <span className="text-muted"> ({periodLabel(d.source.latestPeriod)})</span> : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* What is driving price changes - a trend per part */}
      {o && o.partTrends && o.partTrends.length ? (
        <div className="mb-4">
          <h6 style={{ fontWeight: 600 }}>What is Driving Price Changes?</h6>
          <div className="row">
            {o.partTrends.map((t) => (
              <div key={t.key} className="col-sm-6 mb-2">
                <div className="card h-100" style={{ boxShadow: "none", border: `1px solid ${PART_STYLE[t.key].color}55` }}>
                  <div className="card-body p-2">
                    <b className="small">
                      {PART_STYLE[t.key].icon} {t.label}
                    </b>
                    <MiniChart trend={t} color={PART_STYLE[t.key].color} locked={locked} />
                    <div className="d-flex justify-content-between small">
                      <span>
                        {!locked && typeof t.now === "number" ? (
                          <>
                            <b style={{ color: PART_STYLE[t.key].color }}>{money(t.now)}</b> now
                          </>
                        ) : (
                          <span className="text-muted">index, today = 100</span>
                        )}
                      </span>
                      {!locked && typeof t.projected === "number" ? <span className="text-muted">Proj. (4 quarters): {money(t.projected)}</span> : null}
                    </div>
                    <div className="small text-muted">{t.driver ? <Source s={t.driver} /> : "No monthly index for this part yet."}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Reference prices */}
      {references.length ? (
        <>
          <h6 style={{ fontWeight: 600 }}>Reference prices behind the parts</h6>
          <div className="table-responsive mb-4">
            <table className="table table-sm" style={{ background: "#fff", fontSize: 13 }}>
              <tbody>
                {references.map((r) => (
                  <tr key={r.sourceId}>
                    <td className="text-muted">{r.part}</td>
                    <td>
                      <Source s={r} />
                    </td>
                    <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                      {refValue(r)}
                    </td>
                    <td className="text-muted" style={{ whiteSpace: "nowrap" }}>
                      {periodLabel(r.sourceAsOf)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {/* Data availability note */}
      {!o ? <p className="text-muted small">No monthly price index is published for this item's parts yet, so there is no history or projection.</p> : null}
      {data.notes && data.notes.length ? (
        <ul className="small text-muted pl-3">
          {data.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default ConstructionCostDashboard;
